import type { Location } from "react-router-dom";
import { buildAppWindowUrl } from "@/modules/window/build-url";
import { clientEnvModule } from "@/modules/client-env";
import { SubscriptionPlanKind } from "@/store/subscriptions/types";
import { isCommercialPlanKey, type CommercialPlanKey } from "@/modules/commercial-plans";
import { STRIPE_DESKTOP_RETURN_ROUTE_PATH, StripeDesktopReturnQueryParams } from "./desktop-return";

/**
 *
 * After a user completes or cancels a Stripe flow (checkout or billing portal),
 * Stripe redirects them back to our application using the return URLs we provided.
 * Since Stripe doesn't maintain session state across redirects, we use URL query
 * parameters to communicate the result of the flow back to our app.
 *
 * For example:
 * - After successful checkout: https://app.mem.ai/?stripe_success=1&session_id=cs_...
 * - After cancelled checkout: https://app.mem.ai/?stripe_cancel=1
 * - After billing portal: https://app.mem.ai/?stripe_billing_return=1&has_mem_pro=1
 *
 * Convention: We use "1" to indicate truthiness for boolean parameters.
 * This convention is used throughout the codebase for consistency and simplicity,
 * avoiding the need to parse string values like "true" or "false".
 *
 * The parseStripeQueryParams() function below extracts these parameters and
 * converts them into a structured object that the application can use to
 * determine what UI state to show and what side effects to trigger.
 */

/**
 * Stripe-related URL query parameter constants.
 * These are used for tracking the result of Stripe checkout/billing flows.
 */
export const StripeQueryParams = {
  /**
   * Set to "1" when returning from successful Stripe checkout.
   */
  STRIPE_SUCCESS: "stripe_success",

  /**
   * Contains the Stripe checkout session ID (starts with "cs_").
   * Included in the success URL after checkout completion.
   */
  SESSION_ID: "session_id",

  /**
   * Set to "1" when user cancels the Stripe checkout flow.
   */
  STRIPE_CANCEL: "stripe_cancel",

  /**
   * Set to "1" when returning from the Stripe billing portal.
   */
  STRIPE_BILLING_RETURN: "stripe_billing_return",

  /**
   * Set to "1" when user already has Mem Pro subscription.
   * Used in combination with STRIPE_BILLING_RETURN.
   */
  HAS_MEM_PRO: "has_mem_pro",

  /**
   * Present when Stripe portal was opened for an already-paid user.
   */
  CURRENT_PLAN_KIND: "current_plan_kind",

  /**
   * Present when Stripe portal was opened for an already-paid user.
   */
  REQUESTED_PLAN_KIND: "requested_plan_kind",

  /**
   * Identifies the product flow that started the checkout.
   */
  CHECKOUT_CONTEXT: "checkout_context",

  /**
   * Preserves the billing plan at the start of the checkout funnel.
   */
  ORIGINAL_PLAN_KIND: "original_plan_kind",

  /**
   * Preserves the effective plan at the start of the checkout funnel.
   */
  ORIGINAL_EFFECTIVE_PLAN_KIND: "original_effective_plan_kind",

  /**
   * Identifies the in-app surface that started a checkout.
   */
  CHECKOUT_SOURCE: "checkout_source",

  /**
   * Identifies the onboarding step that started a checkout.
   */
  CHECKOUT_ONBOARDING_STEP: "checkout_onboarding_step",

  /** Exact commercial catalog generation used to start checkout. */
  COMMERCIAL_CATALOG_GENERATION: "commercial_catalog_generation",

  /** Exact commercial plan selected before checkout. */
  TARGET_COMMERCIAL_PLAN_KEY: "target_commercial_plan_key",
} as const;

export const StripeCheckoutContext = {
  Commercial: "commercial",
  Proactive: SubscriptionPlanKind.PROACTIVE,
} as const;
export type StripeCheckoutContext =
  (typeof StripeCheckoutContext)[keyof typeof StripeCheckoutContext];

export const StripeCheckoutSource = {
  Onboarding: "onboarding",
} as const;
export type StripeCheckoutSource = (typeof StripeCheckoutSource)[keyof typeof StripeCheckoutSource];

export const StripeCheckoutOnboardingStep = {
  ExistingUserProactiveTrial: "existing-user-proactive-trial",
} as const;
export type StripeCheckoutOnboardingStep =
  (typeof StripeCheckoutOnboardingStep)[keyof typeof StripeCheckoutOnboardingStep];

export const UpgradePlanQueryParams = {
  UPGRADE_PLAN: "upgrade-plan",
  CALLBACK: "callback",
} as const;

export const UpgradePlanKind = {
  Proactive: SubscriptionPlanKind.PROACTIVE,
} as const;
export type UpgradePlanKind = (typeof UpgradePlanKind)[keyof typeof UpgradePlanKind];

const UPGRADE_PLAN_CALLBACK_ALLOWED_PREFIXES = ["mem://", "https://mem.ai/"] as const;

/**
 * Helper functions for working with Stripe query parameters.
 */

/**
 * Checks if a query parameter is set to "1".
 */
function isParamTrue(value: string | null): boolean {
  return value === "1";
}

/**
 * Checks if a string is a valid Stripe checkout session ID.
 * Stripe checkout session IDs start with "cs_".
 */
function isStripeCheckoutSessionId(value: string | null): boolean {
  return value !== null && value.startsWith("cs_");
}

function parseCheckoutContext(value: string | null): StripeCheckoutContext | undefined {
  if (value === StripeCheckoutContext.Commercial) {
    return StripeCheckoutContext.Commercial;
  }

  if (value === StripeCheckoutContext.Proactive) {
    return StripeCheckoutContext.Proactive;
  }

  return undefined;
}

function parseCheckoutSource(value: string | null): StripeCheckoutSource | undefined {
  if (value === StripeCheckoutSource.Onboarding) {
    return StripeCheckoutSource.Onboarding;
  }

  return undefined;
}

function parseCheckoutOnboardingStep(
  value: string | null
): StripeCheckoutOnboardingStep | undefined {
  if (value === StripeCheckoutOnboardingStep.ExistingUserProactiveTrial) {
    return StripeCheckoutOnboardingStep.ExistingUserProactiveTrial;
  }

  return undefined;
}

function parseSubscriptionPlanKind(value: string | null): SubscriptionPlanKind | undefined {
  if ((Object.values(SubscriptionPlanKind) as string[]).includes(value ?? "")) {
    return value as SubscriptionPlanKind;
  }

  return undefined;
}

function parseCommercialPlanKey(value: string | null): CommercialPlanKey | undefined {
  return value && isCommercialPlanKey(value) ? value : undefined;
}

/**
 * Upgrade-plan callbacks may come from unauthenticated web URLs, so only app
 * deep links and first-party Mem web URLs can be invoked after the modal exits.
 */
function isAllowedUpgradePlanCallbackUrl(value: string): boolean {
  return UPGRADE_PLAN_CALLBACK_ALLOWED_PREFIXES.some(prefix => value.startsWith(prefix));
}

const parseCallbackUrl = (value: string | null): string | null => {
  if (!value) {
    return null;
  }

  try {
    const callbackUrl = new URL(value).toString();

    if (!isAllowedUpgradePlanCallbackUrl(callbackUrl)) {
      return null;
    }

    return callbackUrl;
  } catch {
    return null;
  }
};

export function parseUpgradePlanQueryParams(location: Location) {
  const params = new URLSearchParams(location.search);
  const upgradePlan = params.get(UpgradePlanQueryParams.UPGRADE_PLAN);
  const planKind = upgradePlan === UpgradePlanKind.Proactive ? UpgradePlanKind.Proactive : null;
  const callbackUrl = parseCallbackUrl(params.get(UpgradePlanQueryParams.CALLBACK));

  return {
    isProactiveUpgradePlanFlow: planKind === UpgradePlanKind.Proactive,
    planKind,
    callbackUrl,
    paramsToRemove: [UpgradePlanQueryParams.UPGRADE_PLAN, UpgradePlanQueryParams.CALLBACK],
  };
}

/**
 * Parses Stripe-related query parameters from a Location object.
 */
export function parseStripeQueryParams(location: Location) {
  const params = new URLSearchParams(location.search);

  const stripeSuccess = params.get(StripeQueryParams.STRIPE_SUCCESS);
  const sessionId = params.get(StripeQueryParams.SESSION_ID);
  const stripeCancel = params.get(StripeQueryParams.STRIPE_CANCEL);
  const stripeBillingReturn = params.get(StripeQueryParams.STRIPE_BILLING_RETURN);
  const hasMemPro = params.get(StripeQueryParams.HAS_MEM_PRO);
  const currentPlanKind = parseSubscriptionPlanKind(
    params.get(StripeQueryParams.CURRENT_PLAN_KIND)
  );
  const requestedPlanKind = parseSubscriptionPlanKind(
    params.get(StripeQueryParams.REQUESTED_PLAN_KIND)
  );
  const checkoutContext = parseCheckoutContext(params.get(StripeQueryParams.CHECKOUT_CONTEXT));
  const checkoutSource = parseCheckoutSource(params.get(StripeQueryParams.CHECKOUT_SOURCE));
  const checkoutOnboardingStep = parseCheckoutOnboardingStep(
    params.get(StripeQueryParams.CHECKOUT_ONBOARDING_STEP)
  );
  const originalPlanKind = parseSubscriptionPlanKind(
    params.get(StripeQueryParams.ORIGINAL_PLAN_KIND)
  );
  const originalEffectivePlanKind = parseSubscriptionPlanKind(
    params.get(StripeQueryParams.ORIGINAL_EFFECTIVE_PLAN_KIND)
  );
  const commercialCatalogGeneration =
    params.get(StripeQueryParams.COMMERCIAL_CATALOG_GENERATION) ?? undefined;
  const targetCommercialPlanKey = parseCommercialPlanKey(
    params.get(StripeQueryParams.TARGET_COMMERCIAL_PLAN_KEY)
  );

  return {
    /**
     * True if the user successfully completed Stripe checkout.
     * Checks both stripe_success=1 and presence of a valid session_id.
     */
    isCheckoutSuccess: isParamTrue(stripeSuccess) || isStripeCheckoutSessionId(sessionId),

    /**
     * True if the user cancelled Stripe checkout.
     */
    isCheckoutCancel: isParamTrue(stripeCancel),

    /**
     * True if returning from Stripe billing portal.
     */
    isBillingReturn: isParamTrue(stripeBillingReturn),

    /**
     * True if the user already has Mem Pro (used with billing portal return).
     */
    hasMemPro: isParamTrue(hasMemPro),
    currentPlanKind,
    requestedPlanKind,
    originalPlanKind,
    originalEffectivePlanKind,
    commercialCatalogGeneration,
    targetCommercialPlanKey,

    /**
     * True when returning from the Mem Proactive checkout flow.
     */
    isProactiveCheckout: checkoutContext === StripeCheckoutContext.Proactive,

    /**
     * True when returning from a checkout keyed by the commercial plan catalog.
     */
    isCommercialCheckout: checkoutContext === StripeCheckoutContext.Commercial,

    checkoutContext,
    checkoutSource,
    checkoutOnboardingStep,

    /**
     * The Stripe checkout session ID, if present.
     */
    sessionId,

    /**
     * List of query param keys to remove from the URL.
     */
    paramsToRemove: [
      StripeQueryParams.STRIPE_SUCCESS,
      StripeQueryParams.SESSION_ID,
      StripeQueryParams.STRIPE_CANCEL,
      StripeQueryParams.STRIPE_BILLING_RETURN,
      StripeQueryParams.HAS_MEM_PRO,
      StripeQueryParams.CURRENT_PLAN_KIND,
      StripeQueryParams.REQUESTED_PLAN_KIND,
      StripeQueryParams.CHECKOUT_CONTEXT,
      StripeQueryParams.ORIGINAL_PLAN_KIND,
      StripeQueryParams.ORIGINAL_EFFECTIVE_PLAN_KIND,
      StripeQueryParams.CHECKOUT_SOURCE,
      StripeQueryParams.CHECKOUT_ONBOARDING_STEP,
      StripeQueryParams.COMMERCIAL_CATALOG_GENERATION,
      StripeQueryParams.TARGET_COMMERCIAL_PLAN_KEY,
    ].filter(Boolean) as string[],
  };
}

/**
 * Helper function to append query parameters to a URL.
 */
function appendQueryParams(baseUrl: string, params: Record<string, string>): string {
  const url = new URL(baseUrl);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });
  return url.toString();
}

interface BuildStripeUrlParams {
  location: Location;
  checkoutContext?: StripeCheckoutContext;
  checkoutOnboardingStep?: StripeCheckoutOnboardingStep;
  checkoutSource?: StripeCheckoutSource;
  originalPlanKind?: SubscriptionPlanKind;
  originalEffectivePlanKind?: SubscriptionPlanKind;
  commercialCatalogGeneration?: string;
  targetCommercialPlanKey?: CommercialPlanKey;
}

/**
 * Builds the base return URL from a location.
 * This is the URL the user will return to after Stripe flows.
 */
export function buildStripeReturnUrl({
  location,
  checkoutContext,
  checkoutOnboardingStep,
  checkoutSource,
  originalPlanKind,
  originalEffectivePlanKind,
  commercialCatalogGeneration,
  targetCommercialPlanKey,
}: BuildStripeUrlParams): string {
  if (clientEnvModule.isDesktop()) {
    const baseUrl = buildAppWindowUrl({
      path: STRIPE_DESKTOP_RETURN_ROUTE_PATH,
    }).toString();

    return appendStripeFunnelContext(
      appendQueryParams(baseUrl, {
        [StripeDesktopReturnQueryParams.DESKTOP_RETURN_PATH]:
          location.pathname + (location.search || ""),
      }),
      {
        checkoutContext,
        checkoutOnboardingStep,
        checkoutSource,
        originalPlanKind,
        originalEffectivePlanKind,
        commercialCatalogGeneration,
        targetCommercialPlanKey,
      }
    );
  }

  const baseUrl = buildAppWindowUrl({
    path: location.pathname + (location.search || ""),
  }).toString();

  return appendStripeFunnelContext(baseUrl, {
    checkoutContext,
    checkoutOnboardingStep,
    checkoutSource,
    originalPlanKind,
    originalEffectivePlanKind,
    commercialCatalogGeneration,
    targetCommercialPlanKey,
  });
}

/**
 * Builds the success URL for Stripe checkout.
 * Includes stripe_success=1 and session_id placeholder.
 */
export function buildStripeSuccessUrl({
  location,
  checkoutContext,
  checkoutOnboardingStep,
  checkoutSource,
  originalPlanKind,
  originalEffectivePlanKind,
  commercialCatalogGeneration,
  targetCommercialPlanKey,
}: BuildStripeUrlParams): string {
  const baseUrl = buildStripeReturnUrl({
    location,
    checkoutContext,
    checkoutOnboardingStep,
    checkoutSource,
    originalPlanKind,
    originalEffectivePlanKind,
    commercialCatalogGeneration,
    targetCommercialPlanKey,
  });
  return appendStripeFunnelContext(
    appendQueryParams(baseUrl, {
      [StripeQueryParams.STRIPE_SUCCESS]: "1",
      [StripeQueryParams.SESSION_ID]: "{CHECKOUT_SESSION_ID}",
    }),
    {
      checkoutContext,
      checkoutOnboardingStep,
      checkoutSource,
      originalPlanKind,
      originalEffectivePlanKind,
      commercialCatalogGeneration,
      targetCommercialPlanKey,
    }
  );
}

/**
 * Adds checkout context to a URL when the checkout was started from a product-specific flow.
 */
function appendStripeFunnelContext(
  baseUrl: string,
  {
    checkoutContext,
    checkoutOnboardingStep,
    checkoutSource,
    originalPlanKind,
    originalEffectivePlanKind,
    commercialCatalogGeneration,
    targetCommercialPlanKey,
  }: Pick<
    BuildStripeUrlParams,
    | "checkoutContext"
    | "checkoutOnboardingStep"
    | "checkoutSource"
    | "originalPlanKind"
    | "originalEffectivePlanKind"
    | "commercialCatalogGeneration"
    | "targetCommercialPlanKey"
  >
): string {
  const params: Record<string, string> = {};

  if (checkoutContext) {
    params[StripeQueryParams.CHECKOUT_CONTEXT] = checkoutContext;
  }

  if (checkoutSource) {
    params[StripeQueryParams.CHECKOUT_SOURCE] = checkoutSource;
  }

  if (checkoutOnboardingStep !== undefined) {
    params[StripeQueryParams.CHECKOUT_ONBOARDING_STEP] = checkoutOnboardingStep;
  }

  if (originalPlanKind) {
    params[StripeQueryParams.ORIGINAL_PLAN_KIND] = originalPlanKind;
  }

  if (originalEffectivePlanKind) {
    params[StripeQueryParams.ORIGINAL_EFFECTIVE_PLAN_KIND] = originalEffectivePlanKind;
  }

  if (commercialCatalogGeneration) {
    params[StripeQueryParams.COMMERCIAL_CATALOG_GENERATION] = commercialCatalogGeneration;
  }

  if (targetCommercialPlanKey) {
    params[StripeQueryParams.TARGET_COMMERCIAL_PLAN_KEY] = targetCommercialPlanKey;
  }

  if (Object.keys(params).length === 0) {
    return baseUrl;
  }

  return appendQueryParams(baseUrl, params);
}

/**
 * Builds the cancel URL for Stripe checkout.
 * Includes stripe_cancel=1.
 */
export function buildStripeCancelUrl({
  location,
  checkoutContext,
  checkoutOnboardingStep,
  checkoutSource,
  originalPlanKind,
  originalEffectivePlanKind,
  commercialCatalogGeneration,
  targetCommercialPlanKey,
}: BuildStripeUrlParams): string {
  const baseUrl = buildStripeReturnUrl({
    location,
    checkoutContext,
    checkoutOnboardingStep,
    checkoutSource,
    originalPlanKind,
    originalEffectivePlanKind,
    commercialCatalogGeneration,
    targetCommercialPlanKey,
  });
  return appendStripeFunnelContext(
    appendQueryParams(baseUrl, {
      [StripeQueryParams.STRIPE_CANCEL]: "1",
    }),
    {
      checkoutContext,
      checkoutOnboardingStep,
      checkoutSource,
      originalPlanKind,
      originalEffectivePlanKind,
      commercialCatalogGeneration,
      targetCommercialPlanKey,
    }
  );
}

interface BuildStripeBillingReturnUrlParams extends BuildStripeUrlParams {
  /**
   * Whether to include the has_mem_pro=1 parameter.
   */
  hasMemPro?: boolean;
}

/**
 * Builds the return URL for Stripe billing portal.
 * Optionally includes has_mem_pro=1.
 */
export function buildStripeBillingReturnUrl({
  location,
  hasMemPro = false,
  checkoutContext,
  checkoutOnboardingStep,
  checkoutSource,
  originalPlanKind,
  originalEffectivePlanKind,
  commercialCatalogGeneration,
  targetCommercialPlanKey,
}: BuildStripeBillingReturnUrlParams): string {
  const baseUrl = buildStripeReturnUrl({
    location,
    checkoutContext,
    checkoutOnboardingStep,
    checkoutSource,
    originalPlanKind,
    originalEffectivePlanKind,
    commercialCatalogGeneration,
    targetCommercialPlanKey,
  });
  const params: Record<string, string> = {
    [StripeQueryParams.STRIPE_BILLING_RETURN]: "1",
  };

  if (hasMemPro) {
    params[StripeQueryParams.HAS_MEM_PRO] = "1";
  }

  return appendStripeFunnelContext(appendQueryParams(baseUrl, params), {
    checkoutContext,
    checkoutOnboardingStep,
    checkoutSource,
    originalPlanKind,
    originalEffectivePlanKind,
    commercialCatalogGeneration,
    targetCommercialPlanKey,
  });
}
